/**
 * Bachcha — pricing tiles + Razorpay checkout entry.
 *
 * Design tokens from bachcha/index.html:
 *   --sage      #5a6e4a  --sage-dark  #3d4e30
 *   --peach     #dba287  --peach-light #f7e3d4
 *   --cream     #faf6ee  --ash        #8a8175
 *   --rule      #d4cdb8  --ink        #1f1a0f
 *   Font: Mukta + Crimson Pro (serif display)
 */
import { PRICING, openCheckout } from '../chassis/razorpay.ts';

const TPL = /* html */ `
<style>
:host {
  display: block;
  margin: 48px 0 32px;
}
.shell {
  border: 2px solid #d4cdb8;
  background: linear-gradient(180deg, #fdf9f2 0%, #faf6ee 100%);
  padding: 36px 28px 28px;
  border-radius: 8px;
}
.head {
  text-align: center;
  margin-bottom: 28px;
}
.eyebrow {
  font-family: 'Mukta', sans-serif;
  font-size: 13px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #3d4e30;
  font-weight: 600;
}
h3 {
  font-family: 'Crimson Pro', Georgia, serif;
  font-size: 32px;
  font-style: italic;
  font-weight: 700;
  margin: 8px 0 6px;
  color: #1f1a0f;
  line-height: 1.15;
}
.tagline {
  font-family: 'Mukta', sans-serif;
  font-size: 16px;
  color: #8a8175;
  line-height: 1.4;
}
.tiers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
}
.tier {
  background: #fff;
  border: 2px solid #d4cdb8;
  border-radius: 8px;
  padding: 22px 16px;
  display: grid;
  gap: 10px;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;
}
.tier:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(90,110,74,.10); }
.tier.recommended { border-color: #5a6e4a; }
.tier.recommended::before {
  content: 'recommended';
  display: block;
  font-family: 'Mukta', sans-serif;
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #fff;
  background: #5a6e4a;
  border-radius: 4px 4px 0 0;
  padding: 4px 8px;
  margin: -22px -16px 10px;
  text-align: center;
}
.tier .label {
  font-family: 'Mukta', sans-serif;
  font-size: 13px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #8a8175;
  font-weight: 600;
}
.tier .price {
  font-family: 'Crimson Pro', Georgia, serif;
  font-size: 36px;
  font-style: italic;
  color: #3d4e30;
  font-weight: 700;
  line-height: 1;
}
.tier .per {
  font-family: 'Mukta', sans-serif;
  font-size: 13px;
  color: #8a8175;
}
.tier .desc {
  font-family: 'Mukta', sans-serif;
  font-size: 14px;
  color: #4a3f2e;
  line-height: 1.45;
}
.tier button {
  appearance: none;
  font-family: 'Mukta', sans-serif;
  font-size: 15px;
  font-weight: 700;
  padding: 12px 10px;
  min-height: 44px;
  background: #1f1a0f;
  color: #faf6ee;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 6px;
  transition: background 100ms;
}
.tier button:hover { background: #3d4e30; }
.tier.recommended button { background: #5a6e4a; }
.tier.recommended button:hover { background: #3d4e30; }
.footer {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed #d4cdb8;
  font-family: 'Mukta', sans-serif;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-align: center;
  color: #8a8175;
}
.success {
  margin-top: 18px;
  padding: 14px;
  background: rgba(90, 174, 90, 0.1);
  border-left: 4px solid #4a9a4a;
  font-family: 'Mukta', sans-serif;
  font-size: 15px;
  color: #1f1a0f;
  display: none;
  border-radius: 0 4px 4px 0;
}
.success.show { display: block; }
</style>
<section class="shell">
  <div class="head">
    <div class="eyebrow">Mulya · Pricing · MMXXVI</div>
    <h3>Bacche ka pehla journal. Private. Encrypted. Forever.</h3>
    <div class="tagline">Pregnancy se age 5 tak — milestones, album, time capsule, father's journal.</div>
  </div>
  <div class="tiers">
    <div class="tier" data-tier="free">
      <div class="label">Free</div>
      <div class="price">₹0</div>
      <div class="per">forever</div>
      <div class="desc">1 kid profile · milestones + growth + vaccine · pregnancy timeline · 30 photo cap.</div>
      <button data-go="free">Start free</button>
    </div>
    <div class="tier recommended" data-tier="pro_yearly">
      <div class="label">Pro · Yearly</div>
      <div class="price">₹499</div>
      <div class="per">per year</div>
      <div class="desc">3 kid profiles · unlimited album · Garbh-Kavi journal · Baby-LLM time capsule · Paeds Rx check.</div>
      <button data-go="pro_yearly">Pro khareedein · ₹499</button>
    </div>
    <div class="tier" data-tier="family_yearly">
      <div class="label">Family · Yearly</div>
      <div class="price">₹2,999</div>
      <div class="per">per year · 5 kids</div>
      <div class="desc">5 kid profiles · family circles · grandparent share · Shamir-protected lineage archive.</div>
      <button data-go="family_yearly">Family khareedein · ₹2,999</button>
    </div>
    <div class="tier" data-tier="bundle_yearly">
      <div class="label">Empire Bundle</div>
      <div class="price">₹2,999</div>
      <div class="per">all 8 apps · 1 year</div>
      <div class="desc">Bachcha + Sehat + Sarathi + Hisaab + Wasiyat + sab — puri family ke liye.</div>
      <button data-go="bundle_yearly">Bundle khareedein · ₹2,999</button>
    </div>
  </div>
  <div class="success" id="success"></div>
  <div class="footer">
    Razorpay live · 30-day money back · DPDP 2023 sensitive-personal-data tier · No analytics on pregnancy users
  </div>
</section>
`;

export class BachchaPayCta extends HTMLElement {
  readonly #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const success = this.#shadow.getElementById('success') as HTMLDivElement;
    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-go]').forEach((b) => {
      b.addEventListener('click', async () => {
        const tier = b.dataset.go;
        if (tier === 'free' || typeof tier !== 'string') return;
        if (tier !== 'pro_yearly' && tier !== 'family_yearly' && tier !== 'bundle_yearly') return;
        try {
          await openCheckout(tier, {}, (pid) => {
            success.textContent = `Payment received · Razorpay id ${pid}. Bachcha Pro activation is on the way.`;
            success.classList.add('show');
            localStorage.setItem('bachcha:tier', tier);
            localStorage.setItem('bachcha:payment_id', pid);
          });
        } catch (e) {
          success.textContent = `Checkout failed · ${e instanceof Error ? e.message : 'unknown'}. Email aditya@taxwalaai.com.`;
          success.classList.add('show');
        }
      });
    });
    void PRICING; // referenced for typecheck
  }
}

customElements.define('bachcha-pay-cta', BachchaPayCta);
